import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import ReorderList from "../components/ReorderList";

const DEFAULT = {
  departments: ["Atendimento", "Branding", "Estratégia", "Mídia", "Tráfego Pago", "Conteúdo/Rede Social", "Produção"],
  work_fronts: ["Reunião", "Branding", "Estratégia", "Mídia", "Tráfego Pago", "Conteúdo", "Produção"],
  piece_types: [
    { key: "post_instagram", label: "Post Instagram", carencia_days: 2, prazo_days: 5, custom_fields: [] },
    { key: "campanha_lancamento", label: "Campanha de Lançamento", carencia_days: 5, prazo_days: 30, custom_fields: [] },
  ],
  carencia_days_default: 2,
};

export default function ClientBriefingConfigEditor() {
  const [cfg, setCfg] = useState(DEFAULT);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/content/kv/client_briefing_config").then(({ data }) => {
      if (data) setCfg({ ...DEFAULT, ...data });
    }).catch(() => {});
  }, []);

  const upd = (k, v) => setCfg({ ...cfg, [k]: v });
  const updList = (k, i, v) => { const a = [...(cfg[k] || [])]; a[i] = v; upd(k, a); };
  const removeIdx = (k, i) => upd(k, cfg[k].filter((_, j) => j !== i));
  const addStr = (k) => upd(k, [...(cfg[k] || []), ""]);
  const updPiece = (i, patch) => { const a = [...(cfg.piece_types || [])]; a[i] = { ...a[i], ...patch }; upd("piece_types", a); };
  const addPiece = () => upd("piece_types", [...(cfg.piece_types || []), { key: `piece_${Date.now()}`, label: "Novo Tipo", carencia_days: 2, prazo_days: 5, custom_fields: [] }]);
  const removePiece = (i) => upd("piece_types", cfg.piece_types.filter((_, j) => j !== i));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content/kv/client_briefing_config", { value: cfg });
      toast.success("Config salva.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">Config do Briefing (DEMANDAR)</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">Personalize departamentos, frentes de trabalho e tipos de peça. Cada tipo de peça tem sua própria carência (dias antes do prazo começar) e prazo estimado.</p>
        </div>
        <button onClick={save} disabled={busy} data-testid="briefing-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> {busy ? "Salvando…" : "Salvar"}</button>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <ListEditor label="Departamentos" items={cfg.departments || []} onAdd={() => addStr("departments")} onChange={(arr) => upd("departments", arr)} testid="departments" />
        <ListEditor label="Frentes de Trabalho" items={cfg.work_fronts || []} onAdd={() => addStr("work_fronts")} onChange={(arr) => upd("work_fronts", arr)} testid="workfronts" />
      </div>

      <section className="border border-black/10 rounded-md p-5 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="font-display text-xl">Tipos de Peça</div>
            <div className="text-xs text-neutral-500">Cada tipo tem carência e prazo estimado próprios. Campos customizados podem ser adicionados via key/label.</div>
          </div>
          <button onClick={addPiece} className="text-xs bg-black text-white px-3 py-2 rounded-full hover:bg-accent-orange"><Plus className="w-3 h-3 inline mr-1" /> Novo Tipo</button>
        </div>
        <div>
          {(cfg.piece_types || []).length === 0 && <div className="text-sm text-neutral-500 border border-dashed border-black/15 rounded p-4 text-center">Nenhum tipo cadastrado.</div>}
          <ReorderList
            items={cfg.piece_types || []}
            onChange={(arr) => upd("piece_types", arr)}
            getKey={(p, i) => p.key || i}
            renderItem={(p, i) => (
              <div className="border border-black/10 rounded p-4">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <label className="text-[11px] text-neutral-500">Chave (interna, sem espaços)</label>
                    <input value={p.key} onChange={(e) => updPiece(i, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[11px] text-neutral-500">Nome (visível)</label>
                    <input value={p.label} onChange={(e) => updPiece(i, { label: e.target.value })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-neutral-500">Carência (dias)</label>
                    <input type="number" min={0} value={p.carencia_days ?? 2} onChange={(e) => updPiece(i, { carencia_days: Number(e.target.value) })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-[11px] text-neutral-500">Prazo Estimado (dias)</label>
                    <input type="number" min={0} value={p.prazo_days ?? 5} onChange={(e) => updPiece(i, { prazo_days: Number(e.target.value) })} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      </section>

      <div className="border border-black/10 rounded-md p-5">
        <label className="text-xs text-neutral-500">Carência default (dias) — se um Tipo de Peça não definir a própria:</label>
        <input type="number" min={0} value={cfg.carencia_days_default ?? 2} onChange={(e) => upd("carencia_days_default", Number(e.target.value))} className="w-24 border border-black/15 px-3 py-2 rounded text-sm ml-3" />
      </div>
    </div>
  );
}

function ListEditor({ label, items, onAdd, onChange, testid }) {
  return (
    <section className="border border-black/10 rounded-md p-5">
      <div className="flex justify-between items-center mb-3">
        <div className="font-display text-xl">{label}</div>
        <button onClick={onAdd} className="text-xs bg-black text-white px-3 py-1.5 rounded-full hover:bg-accent-orange" data-testid={`briefing-add-${testid}`}><Plus className="w-3 h-3 inline" /></button>
      </div>
      <ReorderList
        items={items}
        onChange={onChange}
        getKey={(it, i) => `${testid}-${i}`}
        renderItem={(it, i) => (
          <input value={it} onChange={(e) => { const a = [...items]; a[i] = e.target.value; onChange(a); }} className="w-full border border-black/15 px-3 py-2 rounded text-sm" data-testid={`briefing-${testid}-${i}`} />
        )}
      />
    </section>
  );
}
